import { cloneElement, Fragment } from 'react';
import { Card, Heading, Notice, Stack, Text, textVariants, vars } from '@etg/wings';
import { css, cx, etiColors } from '@eti/styles';
import { useProperty, useTranslation } from '@eti/providers';
import { constants } from '@eti/utils';
import { FormSection } from 'redux-form';
import { Notification, variants } from '@eti/components';
import { getTravelerDetails } from '../utils/travelerDetails';
import getTravelerFormSectionName from '../utils/travelerFormName';
import { getIncludedBaggageSummary, isProductAvailable } from '../utils/baggageDetails';
import TravelDocument from './travel-document/TravelDocument';
import FrequentFlyer from './frequent-flyer/FrequentFlyer';
import FullName from './traveler-details/FullName';
import Gender from './traveler-details/Gender';
import Age from './Age';
import DateOfBirth from './traveler-details/DateOfBirth';

/*
  DBFTravelDocumentType and TDTravelDocumentType is exactly the same type, but as this component is used by both the current flow and the DBF flow,
  we introduced both types. TDTravelDocumentType will be deleted when we move permanently to DBF flow in the future.
*/
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const showTravelDocument = travelDocument => travelDocument && Object.values(travelDocument).find(value => value === true) || null;

// Calculate the product's position by taking the number
// multiplying 2 (Since we show 2 baggage product for each traveler),
// adding the current product index (0 or 1 for each product),
// and adding 1 to make position 1-based
const getProductPosition = (travelerNumber, productIndex) => {
  return travelerNumber * 2 + (productIndex + 1);
};
const fieldsetStyles = css`
  border: none;
`;
const infoTextStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.75rem;
`;
const mainContactStyles = css`
  color: ${etiColors.grayScale[10]};
`;
const cardStyles = showProductsPerTraveler => css`
  ${showProductsPerTraveler && 'border-bottom-left-radius: 0; border-bottom-right-radius: 0;'}
`;
const baggageSelectionTextStyle = css`
  padding-block-end: 16px;
`;
const baggageNotificationBorderStyle = css`
  border-bottom: none;
  border-top: none;
`;
const baggageSelectionStyle = removeBorderRadius => css`
  border-radius: ${removeBorderRadius ? 0 : `0 0 ${vars.card.borderRadius} ${vars.card.borderRadius}`};
  padding: 0;
  padding-block-end: 1px;
  padding-inline: 16px;
`;
const TravelerForm = _ref => {
  let {
    age,
    ageType,
    bundleData,
    cabinBaggageTripSummary,
    carrierBaggageTexts,
    checkedBaggageSummary,
    children,
    countries,
    dataTestId,
    dateFormat,
    dateOfBirth = {
      shouldShowFormInput: false,
      hasValueFromSelfServiceRebookingOrder: false
    },
    form,
    frequentFlyerCardTypes,
    id,
    isCabinBaggageAvailableOrIncluded,
    isCheckedBaggageAvailableOrIncluded,
    isSelfServiceRebooking,
    isTravelArranger = false,
    onDateOfBirthBlur,
    secondBagProductDetails,
    shouldShowFrequentFlyerCard,
    shouldShowProductsPerTraveler,
    shouldShowTravelDocumentType,
    travelDocument,
    travelerFormProducts = [],
    travelerNumber,
    tripOperatingCarriers = []
  } = _ref;
  const {
    p
  } = useProperty();
  const {
    t
  } = useTranslation();
  const isAdult = ageType === constants.ageTypes.ADULT;
  const isInfant = ageType === constants.ageTypes.INFANT;
  const {
    label: typeLabel
  } = getTravelerDetails(ageType, {
    adult: t('Adult'),
    child: t('Child'),
    infant: t('Infant')
  });
  const isFFOpenByDefault = travelerNumber === 0 && p('TravelerDetails.FirstTravelerFrequentFlyerOpen.Enabled');
  const carriersSpecificUnavailableLabel = p('TravelerDetails.Ancillary.ShowNotOfferedDisclaimer.Carriers');
  const shouldUseCarrierSpecificUnavailableLabel = tripOperatingCarriers.some(tripCarrier => carriersSpecificUnavailableLabel.includes(tripCarrier));
  const showProductsPerTraveler = shouldShowProductsPerTraveler && travelerFormProducts.length > 0;
  const commaSeparator = p('IbeClient.CommaSeparator.Enabled') ? ',' : '';
  const showSeatMapNotAvailable = !isInfant && shouldUseCarrierSpecificUnavailableLabel;
  return /*#__PURE__*/_jsx("section", {
    children: /*#__PURE__*/_jsxs("fieldset", {
      "aria-labelledby": `traveler-heading-${id}`,
      className: fieldsetStyles,
      children: [/*#__PURE__*/_jsx(FormSection, {
        name: getTravelerFormSectionName(id),
        children: /*#__PURE__*/_jsx(Stack, {
          className: "travelerDetailsSection",
          spacing: 24,
          children: /*#__PURE__*/_jsxs(Card, {
            className: cx(cardStyles(showProductsPerTraveler), 'travelerDetailsSection'),
            "data-testid": dataTestId,
            "data-travelerinfoscrollanchor": true,
            children: [/*#__PURE__*/_jsx(Card.Section, {
              children: /*#__PURE__*/_jsxs(Stack, {
                spacing: 24,
                children: [/*#__PURE__*/_jsxs(Stack, {
                  spacing: 4,
                  children: [/*#__PURE__*/_jsxs(Heading, {
                    id: `traveler-heading-${id}`,
                    level: 2,
                    children: [/*#__PURE__*/_jsx("span", {
                      children: t('TravelerDescription.Header', travelerNumber + 1)
                    }), typeLabel && /*#__PURE__*/_jsx("span", {
                      "data-testid": `traveler-${id}-${ageType}`,
                      children: `${commaSeparator} ${typeLabel}`
                    }), !isAdult && /*#__PURE__*/_jsx(Age, {
                      age: age
                    })]
                  }), isInfant && /*#__PURE__*/_jsx(Text, {
                    as: "p",
                    style: {
                      color: '#262626'
                    },
                    variant: textVariants._14,
                    children: t('Searchform.Passengers.Infants.Information')
                  }), isTravelArranger && /*#__PURE__*/_jsx("div", {
                    className: mainContactStyles,
                    "data-testid": "main-contact-person-text",
                    children: t('TravelerDetails.MainContactPerson.Text')
                  }), /*#__PURE__*/_jsx("p", {
                    className: infoTextStyles,
                    children: t('TravelerDetails.Form.MandatoryInformationText')
                  })]
                }), p('IbeClient.TravelerDetails.NameNotification.Enabled') && /*#__PURE__*/_jsx(Notification, {
                  variant: variants.INFORMATION,
                  children: t('TravelerDetails.NameNotification.Text')
                }), /*#__PURE__*/_jsx(Gender, {
                  form: form,
                  headingId: `traveler-heading-${id}`,
                  id: id,
                  isAdult: isAdult,
                  isReadOnly: isSelfServiceRebooking
                }), /*#__PURE__*/_jsx(FullName, {
                  form: form,
                  id: id,
                  isReadOnly: isSelfServiceRebooking
                }), dateOfBirth.shouldShowFormInput && /*#__PURE__*/_jsx(DateOfBirth, {
                  ageType: ageType,
                  dateFormat: dateFormat,
                  form: form,
                  id: id,
                  isReadOnly: isSelfServiceRebooking && dateOfBirth.hasValueFromSelfServiceRebookingOrder,
                  onBlur: onDateOfBirthBlur
                }), shouldShowFrequentFlyerCard && /*#__PURE__*/_jsx(FrequentFlyer, {
                  cardTypes: frequentFlyerCardTypes,
                  dataTestId: `frequent-flyer-traveler-${travelerNumber}-button`,
                  form: form,
                  id: id,
                  isOpenByDefault: isFFOpenByDefault,
                  travelerNumber: travelerNumber
                })]
              })
            }), showTravelDocument(travelDocument) && /*#__PURE__*/_jsx(Card.Section, {
              children: /*#__PURE__*/_jsx(TravelDocument, {
                countries: countries,
                dateFormat: dateFormat,
                form: form,
                id: id,
                requireTravelDocumentExpiryDate: travelDocument === null || travelDocument === void 0 ? void 0 : travelDocument.requireTravelDocumentExpiryDate,
                shouldShowTravelDocumentCpfNumber: travelDocument === null || travelDocument === void 0 ? void 0 : travelDocument.shouldShowTravelDocumentCpfNumber,
                shouldShowTravelDocumentNationality: travelDocument === null || travelDocument === void 0 ? void 0 : travelDocument.shouldShowTravelDocumentNationality,
                shouldShowTravelDocumentPassportNumber: travelDocument === null || travelDocument === void 0 ? void 0 : travelDocument.shouldShowTravelDocumentPassportNumber,
                shouldShowTravelDocumentType: shouldShowTravelDocumentType
              })
            }), carrierBaggageTexts && (carrierBaggageTexts === null || carrierBaggageTexts === void 0 ? void 0 : carrierBaggageTexts.length) > 0 && /*#__PURE__*/_jsx(Card.Section, {
              className: baggageNotificationBorderStyle,
              children: /*#__PURE__*/_jsx(Notification, {
                variant: variants.WARNING,
                children: carrierBaggageTexts.map(baggageText => baggageText)
              })
            })]
          })
        })
      }), showProductsPerTraveler && /*#__PURE__*/_jsxs(Card, {
        className: baggageSelectionStyle(showSeatMapNotAvailable),
        children: [/*#__PURE__*/_jsx(Heading, {
          className: cx('travelerDetailsSection', baggageSelectionTextStyle),
          "data-testid": "baggage-selection-header",
          id: `baggage-selection-header-${id}`,
          level: 2,
          children: t('TravelerForm.BaggageSelection.Header.Text')
        }), children, travelerFormProducts.map((_ref2, index) => {
          let {
            component,
            id: productId,
            code
          } = _ref2;
          return /*#__PURE__*/_jsx(Fragment, {
            children: component && /*#__PURE__*/cloneElement(component, {
              bundleData,
              code,
              includedBaggage: getIncludedBaggageSummary(productId, cabinBaggageTripSummary, checkedBaggageSummary),
              isBaggageAvailableOrIncluded: isProductAvailable(productId, isCheckedBaggageAvailableOrIncluded, isCabinBaggageAvailableOrIncluded),
              isInfant,
              productPosition: getProductPosition(travelerNumber, index),
              secondBagProductDetails,
              travelerId: id,
              travelerNumber,
              shouldUseCarrierSpecificUnavailableLabel
            })
          }, productId);
        })]
      }), showSeatMapNotAvailable && /*#__PURE__*/_jsxs(Card, {
        className: baggageSelectionStyle(),
        children: [/*#__PURE__*/_jsx(Heading, {
          className: cx('travelerDetailsSection'),
          "data-testid": "seating-selection-header",
          level: 2,
          children: t('StepIndicator.SeatSelection.Label')
        }), /*#__PURE__*/_jsx("div", {
          className: css`
                padding: 16px;
              `,
          children: /*#__PURE__*/_jsx(Notice, {
            variant: "information",
            children: t('Product.SeatMap.Not.Available.To.Offer.Text')
          })
        })]
      })]
    })
  });
};
export default TravelerForm;
//# sourceMappingURL=TravelerForm.js.map